import React, { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import axios from 'axios';
import { toast } from 'sonner';
import L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DRAW_COLORS = [
    { hex: '#ef4444', name: 'Rouge (danger)' },
    { hex: '#f97316', name: 'Orange' },
    { hex: '#eab308', name: 'Jaune' },
    { hex: '#22c55e', name: 'Vert (sécurisé)' },
    { hex: '#3b82f6', name: 'Bleu (périmètre)' },
    { hex: '#8b5cf6', name: 'Violet' },
    { hex: '#ec4899', name: 'Rose' },
    { hex: '#18181b', name: 'Noir' },
];

const styleFor = (color) => ({ color, fillColor: color, fillOpacity: 0.3, weight: 3 });

// Outils de dessin (Geoman) : polygones, lignes, rectangles, cercles, reperes — persistes par instance.
const MapDrawTools = ({ token }) => {
    const map = useMap();
    const initialized = useRef(false);
    const [color, setColor] = useState('#ef4444');
    const colorRef = useRef(color);
    const paletteRef = useRef(null);

    useEffect(() => {
        colorRef.current = color;
        map.pm.setGlobalOptions({ pathOptions: styleFor(color), templineStyle: { color }, hintlineStyle: { color, dashArray: [5, 5] } });
    }, [color, map]);

    useEffect(() => {
        if (paletteRef.current) {
            L.DomEvent.disableClickPropagation(paletteRef.current);
        }
    }, []);

    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;
        const headers = { Authorization: `Bearer ${token}` };

        map.pm.setLang('fr');
        map.pm.addControls({
            position: 'topright',
            drawMarker: false, drawCircleMarker: false, drawText: false, cutPolygon: false,
            rotateMode: false, dragMode: true, editMode: true, removalMode: true,
        });
        // Le bouton repere ouvre le panneau des reperes de batiments
        map.pm.Toolbar.createCustomControl({
            name: 'buildingMarker', block: 'draw', title: 'Placer un repère de bâtiment',
            className: 'control-icon leaflet-pm-icon-marker', toggle: false,
            onClick: () => window.dispatchEvent(new Event('civis-building-panel-toggle')),
        });
        map.pm.Toolbar.changeControlOrder(['buildingMarker']);
        map.pm.setGlobalOptions({ pathOptions: styleFor(colorRef.current) });

        const serialize = (layer) => {
            if (layer instanceof L.Circle) {
                const c = layer.getLatLng();
                return { kind: 'circle', circle: { lat: c.lat, lng: c.lng, radius: layer.getRadius() } };
            }
            const kind = layer instanceof L.Polygon ? 'polygon' : layer instanceof L.Polyline ? 'polyline' : 'marker';
            return { kind, geojson: layer.toGeoJSON() };
        };

        const openColorPopup = (layer, latlng) => {
            const container = document.createElement('div');
            container.style.cssText = 'display:flex;flex-direction:column;gap:6px;';
            const title = document.createElement('div');
            title.textContent = 'Couleur du dessin';
            title.style.cssText = 'font-size:11px;font-weight:600;color:#334155;';
            container.appendChild(title);
            const row = document.createElement('div');
            row.style.cssText = 'display:flex;gap:5px;flex-wrap:wrap;';
            DRAW_COLORS.forEach(c => {
                const btn = document.createElement('button');
                btn.title = c.name;
                btn.setAttribute('data-testid', `drawing-color-${c.hex.slice(1)}`);
                btn.style.cssText = `width:22px;height:22px;border-radius:50%;border:2px solid #fff;box-shadow:0 0 0 1px #cbd5e1;background:${c.hex};cursor:pointer;`;
                btn.onclick = async () => {
                    if (layer.setStyle) layer.setStyle(styleFor(c.hex));
                    map.closePopup();
                    if (!layer._drawingId) return;
                    try {
                        await axios.put(`${API}/map/drawings/${layer._drawingId}`, { color: c.hex }, { headers });
                        toast.success('Couleur mise à jour');
                    } catch { toast.error('Erreur sauvegarde couleur'); }
                };
                row.appendChild(btn);
            });
            container.appendChild(row);
            L.popup({ closeButton: true, autoClose: true }).setLatLng(latlng).setContent(container).openOn(map);
        };

        const attachHandlers = (layer) => {
            layer.on('click', (e) => {
                // Ne pas ouvrir la palette pendant les modes edition/suppression/dessin
                if (map.pm.globalEditModeEnabled() || map.pm.globalRemovalModeEnabled() || map.pm.globalDragModeEnabled() || map.pm.globalDrawModeEnabled()) return;
                L.DomEvent.stopPropagation(e);
                openColorPopup(layer, e.latlng);
            });
            layer.on('pm:edit', async () => {
                if (!layer._drawingId) return;
                try {
                    await axios.put(`${API}/map/drawings/${layer._drawingId}`, serialize(layer), { headers });
                } catch { toast.error('Erreur sauvegarde du dessin'); }
            });
            layer.on('pm:remove', async () => {
                if (!layer._drawingId) return;
                try {
                    await axios.delete(`${API}/map/drawings/${layer._drawingId}`, { headers });
                    toast.success('Dessin supprimé');
                } catch { toast.error('Erreur suppression'); }
            });
        };

        const addToMap = (d) => {
            let layer;
            if (d.kind === 'circle' && d.circle) {
                layer = L.circle([d.circle.lat, d.circle.lng], { radius: d.circle.radius, ...styleFor(d.color || '#ef4444') });
            } else if (d.geojson) {
                const gj = L.geoJSON(d.geojson, { style: styleFor(d.color || '#ef4444') });
                layer = gj.getLayers()[0];
            }
            if (!layer) return;
            layer._drawingId = d.id;
            if (d.label) layer.bindTooltip(d.label, { permanent: true, direction: 'center', className: 'drawing-label' });
            layer.addTo(map);
            attachHandlers(layer);
        };

        axios.get(`${API}/map/drawings`, { headers })
            .then(res => res.data.forEach(addToMap))
            .catch(() => {});

        map.on('pm:create', async (e) => {
            const layer = e.layer;
            const currentColor = colorRef.current;
            if (layer.setStyle) layer.setStyle(styleFor(currentColor));
            const label = window.prompt('Nom de la zone / du tracé (optionnel) :') || '';
            try {
                const res = await axios.post(`${API}/map/drawings`, { ...serialize(layer), label, color: currentColor }, { headers });
                layer._drawingId = res.data.id;
                if (label) layer.bindTooltip(label, { permanent: true, direction: 'center', className: 'drawing-label' });
                attachHandlers(layer);
                toast.success('Dessin enregistré' + (label ? ` : ${label}` : ''));
            } catch {
                toast.error("Erreur d'enregistrement");
                map.removeLayer(layer);
            }
        });
    }, [map, token]);

    return (
        <div ref={paletteRef} className="leaflet-bottom leaflet-right" style={{ marginBottom: 24, marginRight: 10, pointerEvents: 'auto' }}>
            <div className="leaflet-control bg-white rounded-md shadow-lg border border-zinc-300 p-2" data-testid="draw-color-palette">
                <p className="text-[10px] font-semibold text-zinc-600 mb-1.5">Couleur de dessin</p>
                <div className="flex gap-1.5">
                    {DRAW_COLORS.map(c => (
                        <button
                            key={c.hex}
                            title={c.name}
                            onClick={() => setColor(c.hex)}
                            data-testid={`palette-color-${c.hex.slice(1)}`}
                            className="rounded-full transition-transform hover:scale-110"
                            style={{
                                width: 22, height: 22, background: c.hex,
                                border: '2px solid #fff',
                                boxShadow: color === c.hex ? `0 0 0 2.5px ${c.hex}` : '0 0 0 1px #cbd5e1',
                            }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MapDrawTools;
